import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { PendingChangeRequestResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetPendingChangeRequestsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

const mapTrpcChangeRequestToGraphQL = (
  trpcData: PendingChangeRequestResponseRow[]
): GetPendingChangeRequestsQuery => {
  return {
    change_request: trpcData.map((pendingChangeRequest) => ({
      ...pendingChangeRequest,
      parent: pendingChangeRequest.parent && {
        ...pendingChangeRequest.parent,
        owners: pendingChangeRequest.parent?.ancestorContributors || [],
        documentFile: pendingChangeRequest.parent?.documentFile && {
          ...pendingChangeRequest.parent.documentFile,
          parent: pendingChangeRequest.parent.documentFile.parent && {
            ...pendingChangeRequest.parent.documentFile.parent,
            owners:
              pendingChangeRequest.parent.documentFile.parent
                ?.ancestorContributors || [],
          },
        },
        issue_assessment: pendingChangeRequest.parent?.issue_assessment && {
          ...pendingChangeRequest.parent.issue_assessment,
          parent: pendingChangeRequest.parent.issue_assessment.parent && {
            ...pendingChangeRequest.parent.issue_assessment.parent,
            owners:
              pendingChangeRequest.parent.issue_assessment.parent
                ?.ancestorContributors || [],
          },
        },
        acceptance: pendingChangeRequest.parent?.acceptance && {
          ...pendingChangeRequest.parent.acceptance,
          parents: pendingChangeRequest.parent.acceptance.parents
            .map((parent) =>
              parent.risk
                ? {
                    risk: {
                      ...parent.risk,
                      owners: parent.risk?.ancestorContributors || [],
                    },
                  }
                : null
            )
            .filter((parent) => parent !== null),
        },
      },
      responses: pendingChangeRequest.responses
        .filter((response) => response.approver !== null)
        .map((response) => ({
          ...response,
          approver: {
            ...response.approver!,
          },
        })),
    })),
  };
};

export const useGetPendingChangeRequests = (
  parentId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.changeRequest.pendingChangeRequests.queryOptions({
      parentId,
    }),
    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcChangeRequestToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
